/**
 * ToolResultCard
 *
 * Renders the outcome of a tool call as a human-readable card. The content
 * comes from ToolCallResponse.content — enriched at DAG egress so entity
 * refs carry both the LLM short-int `id` and the real `uuid`.
 *
 * Shape dispatch (duck-typed on the common response schemas emitted by
 * `registry_tools.py`):
 *   - { id, uuid?, type, name, created: true }   → "Created {type} '{name}'"
 *   - { id, uuid?, type, name, modified: true }  → "Modified {type} '{name}'"
 *   - anything else                              → fallback summary
 *
 * Entity links are built via `buildUrl(type, uuid)` — UUID comes from the
 * egress enrichment, not the LLM.
 */

import React from 'react';
import Link from 'next/link';
import { buildUrl } from '@sourcepilot/ui/framework/data/route-resolver';
import { appendChatParams, useChatUrlParams } from '@sourcepilot/ui/framework/data/url-helpers';
import { iconMap } from '@sourcepilot/ui/framework/ui/atoms/icons';
import { Settings } from 'lucide-react';

type EntityRef = {
  id?: number | string;
  uuid?: string;
  type?: string;
  name?: string;
  created?: boolean;
  modified?: boolean;
  message?: string;
  created_nested?: unknown;
  diff?: Record<string, { before: unknown; after: unknown }>;
  // Stamped by backend egress enrichment — the DAG-root that owns this
  // entity via its composition chain (e.g. a Requirement's Project).
  // `type` is the Neo4j label (e.g. "RFLPProject"); buildUrl maps it to
  // the frontend class name via modelDisplayConfig.
  parent?: { type: string; uuid: string };
  // Optional href-redirect override, also stamped at egress. When
  // present, the card NAMES this entity but the link navigates to
  // `link` instead — used for Constraints, which deep-link to the
  // Requirement they formalise rather than their own bare detail page.
  link?: { type: string; uuid: string; parent?: { type: string; uuid: string } };
};

function formatDiffValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function DiffRows({ diff }: { diff: Record<string, { before: unknown; after: unknown }> }) {
  const entries = Object.entries(diff);
  if (entries.length === 0) return null;
  return (
    <ul className="ml-5 mt-1 space-y-0.5 text-xs">
      {entries.map(([field, { before, after }]) => (
        <li key={field} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-medium text-gray-700">{field}:</span>
          <span className="min-w-0 break-words text-gray-500 line-through">{formatDiffValue(before)}</span>
          <span className="text-gray-400">→</span>
          <span className="min-w-0 break-words text-gray-900">{formatDiffValue(after)}</span>
        </li>
      ))}
    </ul>
  );
}

interface ToolResultCardProps {
  content: unknown;
  isError?: boolean;
  errorMessage?: string | null;
}

function isEntityResponse(c: unknown): c is EntityRef {
  return (
    typeof c === 'object' &&
    c !== null &&
    typeof (c as any).type === 'string'
  );
}

function ModelIcon({ type }: { type: string }) {
  const Icon = iconMap[type.toLowerCase()] ?? Settings;
  return <Icon className="h-3.5 w-3.5 text-gray-600 shrink-0" />;
}

// Present-tense verb labels ("Create", "Modify") matching the tool-name
// style. The outer card's StatusIndicator already carries the past/
// completed meaning via its check / spinner icons.
const VERB_LABEL: Record<'Created' | 'Modified' | 'Linked' | 'Deleted', string> = {
  Created: 'Create',
  Modified: 'Modify',
  Linked: 'Link',
  Deleted: 'Delete',
};

export function EntityLink({
  entity,
  verb,
  variant = 'nested',
}: {
  entity: EntityRef;
  verb: 'Created' | 'Modified' | 'Linked' | 'Deleted';
  // 'primary' — the entity this tool call was about; the outer card
  //   header already shows the tool name ("Modify Project"), so we render
  //   just the name as a link, no redundant icon / type / verb.
  // 'nested' — one of `created[]` / `linked[]` children; show
  //   `[icon] Verb Type: <linked name>` — only the name is the link,
  //   everything else is plain text, no quotes.
  variant?: 'primary' | 'nested';
}) {
  const { type, uuid, name, parent, link } = entity;
  const label = String(name || entity.id || 'item');
  // Use the hook-based chat-params source. Tool-result-cards can render
  // from `/chat/<id>` full-screen routes where the chat id lives in the
  // path, not in `?chat=` — so `buildUrl`'s window.location-search
  // fallback misses it. The hook reads ChatNavContext (set by ChatView)
  // or parses the `/chat/:id` path segment, so this works regardless.
  const chatParams = useChatUrlParams();
  // The displayed name is always this entity's; the HREF may redirect
  // to a `link` override (e.g. a Constraint → its Requirement page).
  const target = link ?? (uuid && type ? { type, uuid, parent } : null);
  const baseHref =
    target && target.uuid && target.type
      ? buildUrl(
          target.type,
          target.uuid,
          target.parent
            ? { model: target.parent.type, id: target.parent.uuid }
            : undefined,
        )
      : null;
  // Reject hrefs that still embed [paramName] placeholders — Next.js's
  // app-router <Link> doesn't accept dynamic templates, and a `parent`
  // wasn't supplied (or didn't fully resolve) so we can't substitute.
  // Fall back to a non-clickable span; the entity is still labeled,
  // just not navigable from this card.
  const hrefIsResolved = baseHref != null && !baseHref.includes("[");
  const href = hrefIsResolved ? appendChatParams(baseHref!, chatParams) : null;

  const linkedName = href ? (
    <Link href={href} className="font-medium text-gray-900 hover:underline">
      {label}
    </Link>
  ) : (
    <span className="font-medium text-gray-900">{label}</span>
  );

  if (variant === 'primary') {
    return linkedName;
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <ModelIcon type={type || ''} />
      <span className="text-gray-700">{VERB_LABEL[verb]}</span>
      <span className="font-medium text-gray-900">{type}</span>
      <span className="text-gray-500">:</span>
      {linkedName}
    </span>
  );
}

/**
 * Render just the primary entity as a standalone link (name, no verb
 * or icon). Used by the outer tool-call renderer to place the entity
 * name inline with the header ("Modify Project — Heavy Lift Drone"),
 * since the header already carries the verb + type via the tool's
 * `descriptive_name`.
 */
export function PrimaryEntityLabel({ content }: { content: unknown }) {
  if (!isEntityResponse(content)) return null;
  const verb: 'Created' | 'Modified' | null = content.modified
    ? 'Modified'
    : content.created === true
      ? 'Created'
      : null;
  if (!verb) return null;
  return <EntityLink entity={content} verb={verb} variant="primary" />;
}

export function ToolResultCard({ content, isError, errorMessage }: ToolResultCardProps) {
  if (isError) {
    return (
      <div className="mt-2 text-xs text-red-600">
        {errorMessage || 'Tool call failed'}
      </div>
    );
  }

  // Delete-with-approval results carry a `deleted: [{uuid, type, name,
  // parent}, ...]` array (egress enrichment stamps `parent`). Render
  // each entry as an EntityLink — same component as create/modify
  // result rendering — instead of falling through to the
  // "N items returned" key-count summary.
  if (
    typeof content === 'object' &&
    content !== null &&
    Array.isArray((content as any).deleted)
  ) {
    const deleted = (content as any).deleted as EntityRef[];
    if (deleted.length > 0) {
      return (
        <ul className="ml-5 mt-2 space-y-0.5">
          {deleted.map((item, idx) => (
            <li key={item.uuid || item.id || idx}>
              <EntityLink entity={item} verb="Deleted" />
            </li>
          ))}
        </ul>
      );
    }
  }

  // User-cancelled approval-required call (e.g. delete with Deny).
  if (
    typeof content === 'object' &&
    content !== null &&
    (content as any).cancelled === true
  ) {
    return (
      <div className="mt-2 text-xs text-gray-500 italic">
        {(content as any).message || 'Cancelled.'}
      </div>
    );
  }

  if (!isEntityResponse(content)) {
    return <FallbackSummary content={content} />;
  }

  // `modified` wins over `created` — a modify-with-nested-creates
  // response carries BOTH `modified: true` on the parent and `created: [...]`
  // listing the new children. Also accept only strict `true` for `created`
  // since `created` may alternatively be the nested-children array.
  const verb: 'Created' | 'Modified' | null = content.modified
    ? 'Modified'
    : content.created === true
      ? 'Created'
      : null;

  if (!verb) {
    return <FallbackSummary content={content} />;
  }

  const nested = Array.isArray((content as any).created)
    ? ((content as any).created as EntityRef[])
    : null;

  // The primary entity's name appears inline in the tool-call header
  // (rendered by the outer renderer via `PrimaryEntityLabel`), so this
  // card just shows the detail: diff rows + nested-created children.
  if (!content.diff && !(nested && nested.length > 0)) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1">
      {content.diff && <DiffRows diff={content.diff} />}
      {nested && nested.length > 0 && (
        <ul className="ml-5 mt-1 space-y-0.5">
          {nested.map((child, idx) => (
            <li key={child.uuid || child.id || idx}>
              <EntityLink entity={child} verb="Created" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FallbackSummary({ content }: { content: unknown }) {
  let count = 0;
  if (Array.isArray(content)) {
    count = content.length;
  } else if (typeof content === 'object' && content !== null) {
    count = Object.keys(content as object).filter((k) => k !== 'explanation').length;
  }
  if (count === 0) return null;
  return (
    <div className="mt-2 text-xs text-gray-600">
      <span className="text-gray-500">Result: </span>
      <span className="font-medium">
        {count} item{count !== 1 ? 's' : ''} returned
      </span>
    </div>
  );
}
